import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { CATEGORIES } from "../lib/categories";
import { toast } from "sonner";
import { useSettings } from "../lib/useSettings";
import SocialLinks, { FollowButton } from "./SocialLinks";
import { Lock } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [year] = useState(new Date().getFullYear());
  const { settings } = useSettings();
  const contact = settings.contact || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("धन्यवाद! आप Bodh Networks से जुड गए हैं.");
      setEmail("");
    } catch {
      toast.error("कुछ गलत हुआ. कृपया पुनः प्रयास करें.");
    }
  };

  return (
    <footer className="mt-24 bg-zinc-950 text-zinc-300" data-testid="site-footer">
      <div className="relative overflow-hidden">
        <div className="absolute -top-32 -left-32 h-72 w-72 rounded-full bodh-gradient opacity-25 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bodh-gradient opacity-25 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Logo size="lg" variant="light" />
            <p className="font-devanagari text-zinc-400 mt-5 leading-relaxed max-w-sm">
              <span className="block text-zinc-200 font-bold mb-1 font-outfit">Not Just News. Awareness.</span>
              {settings.tagline_hi || "खबर से आगे, बोध की ओर"}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <FollowButton network="instagram" />
              <FollowButton network="youtube" />
            </div>
            <form onSubmit={subscribe} className="mt-6 flex bg-white/5 rounded-full p-1 border border-white/10" data-testid="footer-newsletter">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                placeholder="आपका ईमेल"
                data-testid="footer-newsletter-input"
                className="bg-transparent flex-1 px-4 py-2 text-sm outline-none placeholder:text-zinc-500 font-devanagari"
              />
              <button data-testid="footer-newsletter-btn" className="px-4 py-2 rounded-full bodh-gradient text-white text-sm font-outfit font-bold">
                जुडें
              </button>
            </form>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-outfit font-bold text-white uppercase tracking-widest text-xs mb-4">Sections</h4>
            <ul className="space-y-2.5 font-devanagari text-sm">
              {CATEGORIES.slice(0, 8).map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`} className="hover:text-white transition" data-testid={`footer-cat-${c.slug}`}>
                    {c.name_hi}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-outfit font-bold text-white uppercase tracking-widest text-xs mb-4">Company</h4>
            <ul className="space-y-2.5 font-hind text-sm">
              <li><Link to="/p/about" className="hover:text-white" data-testid="footer-about">About Us</Link></li>
              <li><Link to="/p/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/p/careers" className="hover:text-white" data-testid="footer-careers">Careers</Link></li>
              <li><Link to="/p/advertise" className="hover:text-white" data-testid="footer-advertise">Advertise</Link></li>
              <li><Link to="/p/fact-check" className="hover:text-white" data-testid="footer-factcheck">Fact Check</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-outfit font-bold text-white uppercase tracking-widest text-xs mb-4">Legal</h4>
            <ul className="space-y-2.5 font-hind text-sm">
              <li><Link to="/p/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy</Link></li>
              <li><Link to="/p/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link></li>
              <li><Link to="/live" className="hover:text-white" data-testid="footer-live">Live News</Link></li>
              <li className="pt-2 mt-2 border-t border-white/10">
                <Link
                  to="/login"
                  data-testid="footer-login"
                  className="inline-flex items-center gap-1.5 hover:text-white font-outfit font-bold"
                >
                  <Lock className="h-3.5 w-3.5" /> Staff Login
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-outfit font-bold text-white uppercase tracking-widest text-xs mb-4">Follow</h4>
            <SocialLinks variant="light" />
            <div className="mt-5 space-y-2 text-xs text-zinc-400 font-hind">
              {contact.address && (
                <div className="whitespace-pre-line leading-relaxed" data-testid="footer-contact-address">{contact.address}</div>
              )}
              {contact.email && (
                <div data-testid="footer-contact-email">
                  <span className="text-zinc-500">email : </span>
                  <a href={`mailto:${contact.email}`} className="hover:text-white">{contact.email}</a>
                </div>
              )}
              {contact.phone && <div data-testid="footer-contact-phone">{contact.phone}</div>}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500 font-hind">
            <p>© {year} {settings.site_name || "Bodh Networks"}. सर्वाधिकार सुरक्षित.</p>
            <p className="font-outfit">Crafted with awareness — Not Just News. Awareness.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
